import { CheckCircle, Download, Eye, EyeOff, Upload, X } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from '../../utils/axiosConfig';

const ResumeUpload = ({ onResumeUploaded }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedResume, setUploadedResume] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const fileInputRef = useRef(null);

  // Add caching and request management
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const loadingRequestRef = useRef(null);

  // Prevent double uploads (survives strict mode re-mounts)
  const uploadInProgressRef = useRef(false);

  // Memoize the callback to prevent unnecessary re-renders
  const stableOnResumeUploaded = useCallback(
    (resume) => {
      if (onResumeUploaded) {
        onResumeUploaded(resume);
      }
    },
    [onResumeUploaded]
  );

  // Load existing active resume on component mount - with caching
  useEffect(() => {
    // Prevent multiple simultaneous requests and unnecessary re-fetching
    if (hasLoadedOnce || loadingRequestRef.current) {
      return;
    }

    const loadActiveResume = async () => {
      // Mark that we're currently loading
      loadingRequestRef.current = true;

      try {
        const userID = localStorage.getItem('userID');

        if (!userID) {
          setIsLoading(false);
          setHasLoadedOnce(true);
          return;
        }

        try {
          const response = await axios.get(`/api/v1/resume/active/${userID}`);
          setUploadedResume(response.data);
          stableOnResumeUploaded(response.data);
        } catch (axiosError) {
          if (axiosError.response?.status === 404) {
            // No active resume found, which is fine
            console.log('No active resume found');
          } else {
            throw new Error(
              `Failed to load active resume: ${axiosError.response?.status || axiosError.message}`
            );
          }
        }
      } catch (error) {
        console.error('Error loading active resume:', error);
        // Don't show error to user for this, just continue without pre-loaded resume
      } finally {
        setIsLoading(false);
        setHasLoadedOnce(true);
        loadingRequestRef.current = null;
      }
    };

    loadActiveResume();
  }, []); // Remove onResumeUploaded dependency to prevent excessive calls

  // Update parent when resume changes, but only if we have a stable callback
  useEffect(() => {
    if (uploadedResume && hasLoadedOnce) {
      stableOnResumeUploaded(uploadedResume);
    }
  }, [uploadedResume, stableOnResumeUploaded, hasLoadedOnce]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleFileUpload = async (file) => {
    // Prevent double uploads (React Strict Mode protection)
    if (uploadInProgressRef.current) {
      console.log('Upload already in progress, skipping duplicate request');
      return;
    }

    // Validate user_id first
    const userID = localStorage.getItem('userID');
    if (!userID || userID === 'null' || userID === 'undefined') {
      console.log('User not logged in, cannot upload resume');
      return;
    }

    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'text/plain',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];
    if (!allowedTypes.includes(file.type)) {
      alert('Please upload a PDF, DOC, DOCX, or TXT file.');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB.');
      return;
    }

    uploadInProgressRef.current = true;
    setIsUploading(true);

    try {
      // Convert file to base64
      const base64 = await fileToBase64(file);

      const response = await axios.post(`/api/v1/resume/upload?user_id=${userID}`, {
        filename: file.name,
        content_type: file.type,
        file_data: base64,
      });

      setUploadedResume(response.data);

      stableOnResumeUploaded(response.data);

      console.log('Resume uploaded successfully:', response.data);
    } catch (error) {
      console.error('Error uploading resume:', error);
      alert('Failed to upload resume. Please try again.');
    } finally {
      setIsUploading(false);
      uploadInProgressRef.current = false;
      // Clear file input to allow re-selecting the same file
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Remove the data URL prefix (e.g., "data:application/pdf;base64,")
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const downloadResume = async () => {
    if (!uploadedResume) return;

    try {
      const userID = localStorage.getItem('userID');

      const response = await axios.get(
        `/api/v1/resume/download/${uploadedResume.resume_id}?user_id=${userID}`,
        {
          responseType: 'blob',
        }
      );

      // Create blob and download
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = uploadedResume.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading resume:', error);
      alert('Failed to download resume. Please try again.');
    }
  };

  const removeResume = () => {
    setUploadedResume(null);
    setShowPreview(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="w-full">
      {isLoading ? (
        <div className="flex items-center justify-center h-12">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
          <p className="text-sm text-gray-600">Checking for existing resume...</p>
        </div>
      ) : !uploadedResume ? (
        <div className="flex items-center justify-between h-12">
          <div className="flex items-center">
            <Upload className="h-4 w-4 text-gray-600 mr-2" />
            <p className="text-sm text-gray-600">
              Upload your resume for personalized interview questions
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.doc,.docx,.txt"
              onChange={handleFileSelect}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="px-3 py-1.5 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              Choose file
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between h-12">
          <div className="flex items-center">
            <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
            <div>
              <p className="text-sm font-medium text-green-800">{uploadedResume.filename}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={downloadResume}
              className="text-green-600 hover:text-green-800 p-1"
              title="Download original file"
            >
              <Download className="h-4 w-4" />
            </button>
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="text-green-600 hover:text-green-800 p-1"
              title={showPreview ? 'Hide preview' : 'Show preview'}
            >
              {showPreview ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
            <button
              onClick={removeResume}
              className="text-green-600 hover:text-green-800 p-1"
              title="Remove resume"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Preview Section - Only shown when expanded */}
      {showPreview && uploadedResume && (
        <div className="mt-2 p-3 bg-white border border-green-200 rounded-lg">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Resume Preview</h4>
          <div className="mb-3 p-2 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="font-medium text-gray-600">Type:</span>
                <span className="ml-1 text-gray-800">
                  {uploadedResume.content_type.split('/')[1].toUpperCase()}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Status:</span>
                <span className="ml-1 text-green-600 font-medium">
                  {uploadedResume.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>
          {uploadedResume.extracted_text ? (
            <div>
              <h5 className="text-xs font-medium text-gray-600 mb-1">Extracted Content:</h5>
              <div className="max-h-32 overflow-y-auto p-2 bg-gray-50 rounded-lg border">
                <pre className="whitespace-pre-wrap text-xs text-gray-700 font-mono">
                  {uploadedResume.extracted_text}
                </pre>
              </div>
            </div>
          ) : (
            <div className="p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-xs text-yellow-700">
                No text could be extracted from this file. Interview questions won't be
                personalized.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ResumeUpload;
